import React, {useState} from 'react';
import {t} from 'i18next';
import {Box, Stack, Typography} from '@mui/material';
import UnpinIcon from '@mui/icons-material/PushPin';
import PinIcon from '@mui/icons-material/PushPinTwoTone';
import {convertToUTCDate} from '../../../../../utils/formatters';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {SessionSearch} from '../../../types/SessionSearch';
import {parseDate} from '../../../utilities/utilities';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';

export interface ExistingItemProps {
  index: number;
  item: SessionSearch;
  canPin?: boolean;
  onPin?: (index: number, item: SessionSearch) => void;
  onUnpin?: (index: number, item: SessionSearch) => void;
}

const QUERY_TITLE_VISIBILITY_MAX_LENGTH = 35;

const ExistingItem: React.FC<ExistingItemProps> = ({
  index,
  item,
  canPin = true,
  onPin,
  onUnpin,
}) => {
  const [showToast, setShowToast] = useState(false);
  const displayName = item.isSaved
    ? `${t('sessionSearch.listItems.savedSearchPrefix')} ${item.name}`
    : item.name;

  const pin = () => {
    if (canPin) {
      item.isPinned = true;
      onPin && onPin(index, item);
    } else {
      setShowToast(true);
    }
  };

  const handlePinButtonClick = () => {
    pin();
  };

  const handlePinButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      pin();
    }
  };

  const renderPinIcon = () => {
    return (
      <ArrIconButton
        icon={<PinIcon />}
        onClick={handlePinButtonClick}
        onKeyDown={handlePinButtonKeyDown}
        ariaLabel="Pin"
        shape="none"
        size="small"
        tooltipContent="Pin"
        tooltipPlacement="top"
        tooltipTrigger="hover"
        className="icon-pin"
        testId={`list-item-${index}-pin`}
        tabIndex={0}
      />
    );
  };

  const handleUnpinButtonClick = () => {
    item.isPinned = false;
    onUnpin && onUnpin(index, item);
  };

  const handleUnpinButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      item.isPinned = false;
      onUnpin && onUnpin(index, item);
    }
  };

  const renderUnpinIcon = () => {
    return (
      <ArrIconButton
        icon={<UnpinIcon />}
        onClick={handleUnpinButtonClick}
        onKeyDown={handleUnpinButtonKeyDown}
        ariaLabel="Unpin"
        shape="none"
        size="small"
        tooltipContent="Unpin"
        tooltipPlacement="top"
        tooltipTrigger="hover"
        className="icon-unpin"
        testId={`list-item-${index}-unpin`}
        tabIndex={0}
      />
    );
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) {
      return '';
    }

    // Format the bulk of the date as YYYY/MM/DD hh:mm:ss
    let formattedDate = convertToUTCDate(date.toString());

    // Append AM/PM to the formatted string
    const meridien = getAmPmFromDate(date);
    formattedDate = `${formattedDate} ${meridien}`;

    // Return the fully formatted date as:
    // YYYY/MM/DD hh:mm:ss AM/PM
    return formattedDate;
  };

  const getAmPmFromDate = (date: Date): string => {
    const hours = parseDate(date).getHours();
    if (hours >= 12) {
      return 'PM';
    }
    return 'AM';
  };

  return (
    <>
      <Box>
        <Typography
          className="typo-text subhead5_size"
          data-testid={`existing-item-item-name-${index}`}
          title={displayName}
        >
          {displayName.length > QUERY_TITLE_VISIBILITY_MAX_LENGTH
            ? `${displayName.substring(0, QUERY_TITLE_VISIBILITY_MAX_LENGTH)}...`
            : displayName}
        </Typography>
      </Box>
      <Stack direction="row">
        <Box className="label-container">
          {item.lastRuntime && (
            <Typography
              className="typo-text body5_size"
              data-testid={`existing-item-executed-${index}`}
            >
              {t('sessionSearch.listItems.executed', {
                runTime: formatDate(item.lastRuntime),
              })}
            </Typography>
          )}
        </Box>
        <Box className="icon-button-container">
          <Box className="icon-button-container">
            {item.isPinned ? renderUnpinIcon() : renderPinIcon()}
          </Box>
        </Box>
      </Stack>
      {showToast && (
        <ArrToastNotification
          message={t('sessionSearch.listItems.pinWarning')}
          status={[ToastType.Warning]}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};
export default ExistingItem;
